import { apiVersion } from "../dwclient/dwcommonutils";
import { encodeSendObjectToBase58 } from "../dwclient/jwtcomm";
import { getKeySignature, getSymmetricKey, hasSavedSymmetricKey } from "../dataEncryption";
import { splitPath } from "../paths";
import { syncUploadFile } from "./syncupload";
import { initBatch, getNextFile, completeFile, failFile, cancelBatch, getBatchStatus, OrchestratorWebSocket, setCurrentBatchId, setCurrentWebSocket, closeCurrentWebSocket, getCurrentBatchId, } from "./orchestratorClient";
import { saveBatchId, clearBatchId } from "./batchIdStorage";
import { setPlannedUploadPathsAsync, trackUploadedFile, trackUploadingFile, resetUploadState, } from "./uploadworkerutil";
let isUploadCancelled = false;
let currentUploadPromise = null;
export function cancelOrchestratedUpload() {
    isUploadCancelled = true;
    const batchId = getCurrentBatchId();
    if (batchId) {
        cancelBatch(batchId).catch(console.error);
        clearBatchId();
    }
    closeCurrentWebSocket();
}
export function isOrchestratedUploadActive() {
    return currentUploadPromise !== null && !isUploadCancelled;
}
function isMobileBrowser() {
    return typeof Worker === "undefined" || typeof BroadcastChannel === "undefined";
}
async function uploadSingleFile(file, fullPath, onProgress) {
    return new Promise((resolve, reject) => {
        const baseUrl = process.env.DWINITY_BACKEND ?? "https://api.dev.dwinity.com";
        const filesUrl = baseUrl + `/api/${apiVersion}/files`;
        const sendObject = { path: fullPath };
        const message = encodeSendObjectToBase58(sendObject);
        const signature = getKeySignature();
        syncUploadFile(filesUrl, file, message, fullPath, signature, (data) => {
            if (onProgress && data.loaded !== undefined && data.total !== undefined) {
                onProgress(data.loaded, data.total);
            }
        }, () => {
            trackUploadedFile(fullPath);
            resolve();
        }, (data) => {
            if (data?.error) {
                reject(new Error(data.error));
            }
            else {
                resolve();
            }
        });
    });
}
const CHUNK_SIZE = 500;
async function processFilesInChunks(files, basePath, onProgress) {
    const result = [];
    const total = files.length;
    for (let i = 0; i < total; i += CHUNK_SIZE) {
        const chunk = files.slice(i, i + CHUNK_SIZE);
        for (const file of chunk) {
            const relativePath = file.path || file.webkitRelativePath || file.name;
            const fullPath = basePath.endsWith("/")
                ? basePath + relativePath
                : basePath + "/" + relativePath;
            result.push({
                name: file.name,
                path: fullPath,
                size: file.size,
            });
        }
        onProgress?.(Math.min(i + CHUNK_SIZE, total), total);
        if (i + CHUNK_SIZE < total) {
            await new Promise(resolve => setTimeout(resolve, 0));
        }
    }
    return result;
}
export async function startOrchestratedUpload(options) {
    const { provider, files, basePath, graphId, existingBatchId, onFileProgress, onBatchProgress, onFileCompleted, onBatchCompleted, onError, } = options;
    isUploadCancelled = false;
    resetUploadState();
    // Ensure encryption key is initialized before starting uploads
    // This prompts the user to sign if session storage lacks the signature
    if (!hasSavedSymmetricKey()) {
        console.log("Symmetric key not found, initializing with wallet signature...");
        await getSymmetricKey(provider);
    }
    if (process.env.NODE_ENV !== "production") {
        console.log(`Building file metadata for ${files.length} files...`);
    }
    const mutableFiles = [...files];
    const fileMetadataList = await processFilesInChunks(files, basePath, (processed, total) => {
        if (process.env.NODE_ENV !== "production" && (processed % 2000 === 0 || processed === total)) {
            console.log(`Metadata progress: ${processed}/${total}`);
        }
    });
    if (!existingBatchId) {
        if (process.env.NODE_ENV !== "production") {
            console.log(`Registering ${fileMetadataList.length} upload paths...`);
        }
        const allPaths = fileMetadataList.map((f) => f.path);
        await setPlannedUploadPathsAsync(allPaths);
    }
    console.log(`Starting orchestrated upload: ${files.length} files`);
    // NOTE: Caller (FileUploadsModal) already checked isOrchestratorAvailable()
    // No need to check again here - it was causing timeout issues with the second check
    try {
        let batchId;
        let totalFiles;
        let totalBytes;
        if (existingBatchId) {
            batchId = existingBatchId;
            const status = await getBatchStatus(existingBatchId);
            totalFiles = status.totalFiles;
            totalBytes = status.totalBytes;
            saveBatchId(existingBatchId, basePath);
            console.log(`Resuming batch: ${totalFiles} files`);
        }
        else {
            const initResult = await initBatch(fileMetadataList, graphId);
            batchId = initResult.batchId;
            totalFiles = initResult.totalFiles;
            totalBytes = initResult.totalBytes;
            saveBatchId(batchId, basePath);
            console.log(`Batch initialized: ${totalFiles} files`);
        }
        setCurrentBatchId(batchId);
        const wsProgress = new OrchestratorWebSocket(batchId, (status) => {
            onBatchProgress?.(status);
        }, (status) => {
            onBatchCompleted?.(status);
        }, (error) => {
            console.error("WebSocket error:", error);
        });
        setCurrentWebSocket(wsProgress);
        wsProgress.connect();
        currentUploadPromise = processUploadQueue(batchId, mutableFiles, fileMetadataList, onFileProgress, onFileCompleted, onError, onBatchProgress);
        await currentUploadPromise;
        const finalStatus = await getBatchStatus(batchId);
        if (finalStatus.status === "completed" || finalStatus.status === "cancelled") {
            clearBatchId();
            onBatchCompleted?.(finalStatus);
        }
        closeCurrentWebSocket();
        setCurrentBatchId(null);
        currentUploadPromise = null;
        return finalStatus;
    }
    catch (error) {
        console.error("Orchestrated upload error:", error);
        closeCurrentWebSocket();
        setCurrentBatchId(null);
        currentUploadPromise = null;
        throw error;
    }
}
async function processUploadQueue(batchId, files, fileMetadataList, onFileProgress, onFileCompleted, onError, onBatchProgress) {
    let completedFiles = 0;
    const totalFiles = files.length;
    let lastGcTrigger = 0;
    const GC_INTERVAL = 100;
    const filePathMap = new Map();
    for (let i = 0; i < fileMetadataList.length; i++) {
        const file = files[i];
        const metadata = fileMetadataList[i];
        if (file && metadata) {
            filePathMap.set(metadata.path, { file, metadata, localIndex: i });
        }
    }
    while (!isUploadCancelled) {
        const nextFile = await getNextFile(batchId);
        if (nextFile.done) {
            console.log(`Batch complete`);
            break;
        }
        if (nextFile.paused) {
            await new Promise((resolve) => setTimeout(resolve, 2000));
            continue;
        }
        const fileIndex = nextFile.fileIndex;
        const serverFilePath = nextFile.filePath;
        let file = files[fileIndex] || null;
        let metadata = fileMetadataList[fileIndex];
        let localIndex = fileIndex;
        if (!file && serverFilePath) {
            const mapped = filePathMap.get(serverFilePath);
            if (mapped) {
                file = mapped.file;
                metadata = mapped.metadata;
                localIndex = mapped.localIndex;
            }
        }
        if (!file) {
            if (process.env.NODE_ENV !== "production") {
                console.error(`File not found at index ${fileIndex}, path: ${serverFilePath}`);
            }
            await failFile(batchId, fileIndex, "File not found in browser");
            continue;
        }
        const pathParts = splitPath(metadata.path);
        if (pathParts?.name && pathParts.name.startsWith("~$")) {
            await completeFile(batchId, fileIndex, 0);
            files[localIndex] = null;
            filePathMap.delete(metadata.path);
            continue;
        }
        if (process.env.NODE_ENV !== "production" || fileIndex % 100 === 0) {
            console.log(`Uploading file ${fileIndex + 1}/${totalFiles}: ${metadata.name}`);
        }
        trackUploadingFile(metadata.path);
        const fileSize = file.size;
        const fileType = file.type || "";
        try {
            await uploadSingleFile(file, metadata.path, (loaded, total) => {
                onFileProgress?.({
                    fileIndex,
                    fileName: metadata.name,
                    loaded,
                    total,
                    fileSize,
                });
            });
            await completeFile(batchId, fileIndex, fileSize);
            completedFiles++;
            files[localIndex] = null;
            filePathMap.delete(metadata.path);
            if (completedFiles - lastGcTrigger >= GC_INTERVAL) {
                lastGcTrigger = completedFiles;
                await new Promise(resolve => setTimeout(resolve, 0));
            }
            onFileCompleted?.({
                fileIndex,
                fileName: metadata.name,
                filePath: metadata.path,
                fileSize,
                fileType,
            });
            // Note: onBatchProgress is handled by WebSocket connection (OrchestratorWebSocket)
            // Do NOT call it here to avoid duplicate/conflicting progress updates
            if (process.env.NODE_ENV !== "production" || fileIndex % 100 === 0) {
                console.log(`File ${fileIndex + 1}/${totalFiles} completed`);
            }
        }
        catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            console.error(`File ${fileIndex + 1} failed: ${metadata.name}`, error);
            const result = await failFile(batchId, fileIndex, errorMessage, true);
            if (result.retrying) {
                console.log(`Retrying file ${fileIndex + 1} (attempt ${result.retryCount})`);
            }
            else {
                onError?.(error instanceof Error ? error : new Error(errorMessage), fileIndex, metadata.name);
            }
        }
    }
}
async function fallbackDirectUpload(options, fileMetadataList) {
    // Don't use fallback - caller should use worker-based upload instead
    // This prevents memory pressure from loading all files on the main thread
    throw new Error("Orchestrator unavailable. Use worker-based upload instead of orchestrated upload fallback.");
}
export async function getUploadProgress() {
    const batchId = getCurrentBatchId();
    if (!batchId)
        return null;
    try {
        return await getBatchStatus(batchId);
    }
    catch {
        return null;
    }
}
